import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { joiResolver } from "@hookform/resolvers/joi"
import Joi from "joi"
import {
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Icon,
  HStack,
  Skeleton,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  Box,
} from "@chakra-ui/react"
import { PlusIcon, ChevronDownIcon } from "@heroicons/react/solid"
import { PencilIcon, TrashIcon, SearchIcon } from "@heroicons/react/outline"
import { deleteBankByBankId, getBanks, getBankByBankId, storeBank, updateBankByBankId } from "../../../../api/bank"
import { MODAL_TYPE } from "../../../../constants/modal"
import { DeleteAlert } from "../../../../components/Alert"

const schema = Joi.object({
  id: Joi.string().allow(""),
  bankName: Joi.string().required(),
  accountName: Joi.string().required(),
  accountNumber: Joi.string().required(),
})

export default function Bank() {
  const [changeState, setChangeState] = useState(false)
  const [banks, setBanks] = useState({ data: [], error: null, isLoading: false })
  const [bank, setBank] = useState({
    isLoading: false,
    error: null,
  })
  const [formModal, setFormModal] = useState({
    type: MODAL_TYPE.STORE,
    isOpen: false,
    isLoading: false,
    error: null,
  })
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    bankId: "",
    error: null,
    isLoading: false,
  })
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({ resolver: joiResolver(schema) })

  useEffect(() => {
    const GetBanks = async () => {
      setBanks({ ...banks, isLoading: true })
      const result = await getBanks()
      if (result.success === true) {
        setBanks({ ...banks, data: result.data.banks, isLoading: false })
      } else {
        setBanks({ ...banks, error: result.error.message, isLoading: false })
      }
    }
    GetBanks()
    return () => {
      setBanks({ data: [], error: null, isLoading: false })
    }
  }, [changeState])

  const submitStoreBank = async (data) => {
    setFormModal({ ...formModal, isLoading: true })
    const result = await storeBank(data)
    if (result.success === true) {
      setFormModal({ ...formModal, isLoading: false })
      closeModal()
      setChangeState(!changeState)
    } else {
      setFormModal({ ...formModal, isLoading: false, error: result.error.message })
    }
  }
  const submitUpdateBank = async (data) => {
    setFormModal({ ...formModal, isLoading: true })
    const result = await updateBankByBankId(data)
    if (result.success === true) {
      setFormModal({ ...formModal, isLoading: false })
      closeModal()
      setChangeState(!changeState)
    } else {
      setFormModal({ ...formModal, isLoading: false, error: result.error.message })
    }
  }
  const submitDeleteBank = async (id) => {
    setDeleteModal({ ...deleteModal, isLoading: true })
    const result = await deleteBankByBankId(id)
    if (result.success === true) {
      setDeleteModal({ ...deleteModal, isOpen: false, isLoading: false })
      setChangeState(!changeState)
    } else {
      setDeleteModal({ ...deleteModal, error: "Bank gagal dihapus", isLoading: false })
    }
  }
  const closeModal = () => {
    setValue("id", "")
    setValue("bankName", "")
    setValue("accountNumber", "")
    setValue("accountName", "")
    setFormModal({ ...formModal, isOpen: false, error: null })
  }

  const openModal = async (bankId = "", type = MODAL_TYPE.STORE) => {
    setFormModal({ ...formModal, type, isOpen: true })
    if (type === MODAL_TYPE.UPDATE) {
      setBank({ ...bank, isLoading: true })
      const result = await getBankByBankId(bankId)
      if (result.success) {
        const { bankName, accountName, accountNumber, id } = result.data.bank
        setValue("id", id)
        setValue("bankName", bankName)
        setValue("accountName", accountName)
        setValue("accountNumber", accountNumber)
        setBank({ ...bank, isLoading: false })
      }
    }
  }
  const openDeleteModal = (bankId) => {
    setDeleteModal({ ...deleteModal, bankId, isOpen: true })
  }

  return (
    <Flex direction="column" minH="70vh">
      <Flex py="8" justify="space-between" spacing={16} direction={["column", "row"]}>
        <FormControl width={["full", "xs"]}>
          <InputGroup>
            <Input type="text" placeholder="Search" />
            <InputRightElement>
              <Button variant="ghost" _focus={{ boxShadow: "none" }}>
                <Icon as={SearchIcon} color="gray.550" h="4" w="4" />
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>
        <Button onClick={() => openModal()} mt={["2", "0"]}>
          Tambah Bank
          <Icon as={PlusIcon} h="5" w="5" ml="2" />
        </Button>
        <Modal isCentered size="sm" blockScrollOnMount={true} isOpen={formModal.isOpen} onClose={closeModal}>
          <ModalOverlay />
          <ModalContent p="2">
            <ModalHeader>
              <Text color="gray.750">{formModal.type === MODAL_TYPE.STORE ? "Tambah" : "Edit"} Bank</Text>
            </ModalHeader>
            <ModalCloseButton _focus={{ boxShadow: "none" }} />
            <Box px={6}>
              {formModal.error && (
                <Alert size="sm" status="error">
                  <AlertIcon />
                  Bank gagal di{formModal.type === MODAL_TYPE.STORE ? "tambahkan" : "edit"}
                </Alert>
              )}
            </Box>
            <ModalBody my="3">
              <form onSubmit={handleSubmit(formModal.type === MODAL_TYPE.STORE ? submitStoreBank : submitUpdateBank)}>
                {bank.isLoading && (
                  <Stack spacing="3">
                    <Skeleton startColor="gray.100" endColor="gray.200" height="40px" />
                    <Skeleton startColor="gray.100" endColor="gray.200" height="40px" />
                    <Skeleton startColor="gray.100" endColor="gray.200" height="40px" />
                  </Stack>
                )}
                {!bank.isLoading && (
                  <Stack spacing="3">
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.550">
                        Nama Bank
                      </FormLabel>
                      <Input type="text" {...register("bankName")} isInvalid={errors.bankName} />
                      <Text fontSize="sm" color="red.500">
                        {errors.bankName?.message}
                      </Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.550">
                        Nama Pemilik Bank
                      </FormLabel>
                      <Input type="text" {...register("accountName")} isInvalid={errors.accountName} />
                      <Text fontSize="sm" color="red.500">
                        {errors.accountName?.message}
                      </Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.550">
                        Nomor Rekening Bank
                      </FormLabel>
                      <Input type="text" {...register("accountNumber")} isInvalid={errors.accountNumber} />
                      <Text fontSize="sm" color="red.500">
                        {errors.accountNumber?.message}
                      </Text>
                    </FormControl>
                  </Stack>
                )}
              </form>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={closeModal} variant="outline" isDisabled={formModal.isLoading}>
                Batal
              </Button>
              <Button
                onClick={handleSubmit(formModal.type === MODAL_TYPE.STORE ? submitStoreBank : submitUpdateBank)}
                isLoading={formModal.isLoading}
                type="submit"
              >
                Simpan
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
      <Flex overflowX="auto" overflowY="hidden" direction="column">
        {banks.isLoading ? (
          <Stack spacing={6}>
            {[...Array(6)].map((e, i) => (
              <Stack key={i}>
                <Skeleton startColor="gray.100" endColor="gray.200" height="30px" />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Table variant="simple" size="md" mb={4}>
            <Thead>
              <Tr color="gray.100" borderTop="1px">
                <Th fontSize="14px" fontWeight="semibold" color="gray.550">
                  NAMA BANK
                </Th>
                <Th fontSize="14px" fontWeight="semibold" color="gray.550">
                  NAMA PEMILIK
                </Th>
                <Th fontSize="14px" fontWeight="semibold" color="gray.550">
                  NOMOR REKENING
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {banks.data.map((bank) => (
                <Tr key={bank.id} fontSize="sm" color="gray.550">
                  <Td fontWeight="semibold">{bank.bankName}</Td>
                  <Td>{bank.accountName}</Td>
                  <Td>{bank.accountNumber}</Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        px={4}
                        py={2}
                        transition="all 0.2s"
                        borderRadius="md"
                        borderWidth="1px"
                        _focus={{ boxShadow: "none" }}
                        fontWeight="semibold"
                      >
                        Atur
                        <Icon ml="2" as={ChevronDownIcon} h="5" w="5" />
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => openModal(bank.id, MODAL_TYPE.UPDATE)}>
                          <HStack w="full">
                            <Icon as={PencilIcon} h="4" w="4" />
                            <Text>Edit</Text>
                          </HStack>
                        </MenuItem>
                        <MenuItem onClick={() => openDeleteModal(bank.id)}>
                          <HStack w="full">
                            <Icon as={TrashIcon} h="4" w="4" />
                            <Text>Hapus</Text>
                          </HStack>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
            <Tfoot></Tfoot>
          </Table>
        )}
      </Flex>
      <DeleteAlert
        isOpen={deleteModal.isOpen}
        title="Bank"
        isLoading={deleteModal.isLoading}
        action={() => submitDeleteBank(deleteModal.bankId)}
        setOpen={setDeleteModal}
      />
    </Flex>
  )
}
